import ApplicationController from "../turbo/application_controller";
import { changeChecked } from "../lib/dom";

export default class extends ApplicationController {
    static targets = ["grouping"];

    connect() {
        this.observeStaticCheckboxes();
        this.observeDropdowns();
        this.observeCheckboxes();
    }

    observeStaticCheckboxes() {
        const staticCheckboxes = this.targets.element.querySelectorAll("[name='static_toggleable']");

        staticCheckboxes.forEach(checkbox => {
            const availableColumnsObserver = new window.MutationObserver((mutations) => {
                mutations.forEach(this.disableStaticCheckbox);
            });

            availableColumnsObserver.observe(
                checkbox.closest("details").querySelector(".fields ul"),
                { subtree: true, attributes: true, attributeFilter: ["class"] }
            );
        });
    }

    disableStaticCheckbox(e) {
        const hiddenElements = e.target.closest(".fields").querySelectorAll("li.selected");
        const staticToggle = e.target.closest("details").querySelector("[name='static_toggleable']");

        staticToggle.disabled = hiddenElements.length > 0;
    }

    observeDropdowns() {
        const details = this.targets.element.querySelectorAll("details");

        details.forEach(detail => {
            const detailsObserver = new window.MutationObserver((mutations) => {
                mutations.forEach(this.toggleExpandButton);
            });

            detailsObserver.observe(detail, { attributes: true, attributeFilter: ["open"] });
        });
    }

    toggleExpandButton(event) {
        const category = event.target.closest(".category");

        const dropdowns = category.querySelectorAll("details");
        const openDropdowns = Array.from(dropdowns).filter(details => details.open);

        const allOpen = openDropdowns.length === dropdowns.length;

        category.querySelector(".expand-section").classList.toggle("hidden", allOpen);
        category.querySelector(".collapse-section").classList.toggle("hidden", !allOpen);
    }

    observeCheckboxes() {
        const checkboxes = this.fetchFieldCheckboxes();
        checkboxes.forEach(checkbox => {
            checkbox.addEventListener("change", (e) => {
                this.toggleGroupingCheckbox(e);
            });
        });
    }

    toggleGroupingCheckbox(event, groupName) {
        if (event.target.dataset.groupLevel) {
            return;
        }

        const group = event.target.dataset.groupName || groupName;
        const selector = `#${group} .fields li:not(.selected,[hidden]) input[type='checkbox']`;
        const availableGroupingCheckboxes = document.querySelectorAll(selector);
        const checkedGroupingCheckboxes = Array.from(availableGroupingCheckboxes).filter(checkbox => checkbox.checked);
        const countAvailable = availableGroupingCheckboxes.length;
        const countChecked = checkedGroupingCheckboxes.length;

        this.updateSelectedFieldsCounter(event, countChecked);
        const groupingCheckbox = document.getElementById(`${group}_checkbox`);

        if (countAvailable === countChecked && countAvailable > 0) {
            groupingCheckbox.indeterminate = false;
            groupingCheckbox.checked = true;
        } else if (countChecked < countAvailable && countChecked > 0) {
            groupingCheckbox.indeterminate = true;
            groupingCheckbox.checked = false;
        } else {
            groupingCheckbox.indeterminate = false;
            groupingCheckbox.checked = false;
        }
    }

    updateSelectedFieldsCounter(event, count) {
        const counterSpan = event.target.closest("li.expander").querySelector(".selected-fields-counter");
        if (counterSpan) {
            counterSpan.innerHTML = `(${count} selected)`;
        }
    }

    selectField(e) {
        const group = e.params.group;
        e.target.indeterminate = false;

        if (group) {
            const selector = `#${group} .fields li:not(.selected,[hidden]) input[type='checkbox']`;
            const groupCheckboxChecked = e.target.checked;

            document.querySelectorAll(selector).forEach(checkbox => {
                changeChecked(checkbox, groupCheckboxChecked);
            });
        }

        this.toggleOverlay();
    }

    toggleStaticColumns(e) {
        if (e.params.group) {
            const groupingCheckboxChecked = e.target.closest("summary").querySelector("input[type='checkbox']").checked;
            document.querySelectorAll(`#${e.params.group} .fields li`)
                .forEach(field => {
                    field.toggleAttribute("hidden");

                    const checkbox = field.querySelector("input[type='checkbox']");
                    checkbox.checked = field.hidden ? false : groupingCheckboxChecked;
                });

            const checkedCheckboxes = document.querySelectorAll(`#${e.params.group} .fields li:not([hidden]) input[type='checkbox']:checked`);
            this.toggleGroupingCheckbox(e, e.params.group);
            this.updateSelectedFieldsCounter(e, checkedCheckboxes.length);
        }
    }

    selectAll() {
        const checkboxes = this.fetchVisibleCheckboxes();

        checkboxes.forEach(checkbox => changeChecked(checkbox, true));
        this.toggleOverlay();
    }

    deselectAll() {
        const checkboxes = this.fetchVisibleCheckboxes();

        checkboxes.forEach(checkbox => changeChecked(checkbox, false));
        this.toggleOverlay();
    }

    expandGrouping(event) {
        const expandButton = event.currentTarget;
        const category = expandButton.dataset.section;
        const groups = this.fetchGroups(category);

        groups.forEach(group => group.setAttribute("open", true));
        expandButton.classList.add("hidden");
        expandButton.parentElement.querySelector(".collapse-section").classList.remove("hidden");
    }

    collapseGrouping(event) {
        const collapseButton = event.currentTarget;
        const category = collapseButton.dataset.section;
        const groups = this.fetchGroups(category);

        groups.forEach(group => group.removeAttribute("open"));
        collapseButton.classList.add("hidden");
        collapseButton.parentElement.querySelector(".expand-section").classList.remove("hidden");
    }

    collapseAll() {
        this.groupingTargets.forEach(group => group.removeAttribute("open"));
    }

    selectCategory(event) {
        const selectButton = event.currentTarget;
        const category = selectButton.dataset.section;
        const checkboxes = this.fetchVisibleCheckboxes(category);

        checkboxes.forEach(checkbox => changeChecked(checkbox, true));
        this.toggleOverlay();
    }

    deselectCategory(event) {
        const deselectButton = event.currentTarget;
        const category = deselectButton.dataset.section;
        const checkboxes = this.fetchVisibleCheckboxes(category);

        checkboxes.forEach(checkbox => changeChecked(checkbox, false));
        this.toggleOverlay();
    }

    toggleOverlay() {
        const checkedCount = Array.from(this.fetchFieldCheckboxes()).filter(box => box.checked).length;

        document.querySelectorAll(".section-overlay")
            .forEach(overlay => overlay.classList.toggle("active", checkedCount > 0));
    }

    // Different ways checkboxes are made "invisible":
    // 'hidden' class is applied to list items that do not match the filter query
    // +hidden+ attribute is applied to either static or dynamic fields depending
    //    on if static_toggleable is checked
    // 'selected' class is applied to list items that have already been added to
    // the export template
    subfieldSelector = ".fields li:not(.hidden,.selected):not([hidden]) input[type='checkbox']:not([name='static_toggleable'])";
    topLevelFieldSelector = ".ungrouped li:not(.hidden,.selected) input[type='checkbox']"

    checkboxSelector() {
        return `${this.subfieldSelector}, ${this.topLevelFieldSelector}`;
    }

    fetchFieldCheckboxes() {
        return document.getElementById("available").querySelectorAll(".fields input[type='checkbox']:not([name='static_toggleable'])");
    }

    fetchVisibleCheckboxes(category) {
        if (category) {
            return document.getElementById("available")
                .querySelectorAll(`.category[data-section='${category}'] ${this.subfieldSelector}, .category[data-section='${category}'] ${this.topLevelFieldSelector}`);
        }

        return document.getElementById("available").querySelectorAll(this.checkboxSelector());
    }

    fetchGroups(category) {
        return document.getElementById("available")
            .querySelectorAll(`.category[data-section='${category}'] details`);
    }
}
